import { useRef } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { ParentConsentPage } from './account/ParentConsentPage';
import { PublicConsentPage } from './account/PublicConsentPage';
import { MagicLinkPage } from './account/MagicLinkPage';
import { ReactQueryProvider } from './account/queryClient';
import { ResetPasswordPage } from './account/ResetPasswordPage';
import { SuspensionBanner } from './account/SuspensionBanner';
import { BadgeToast } from './badges/BadgeToast';
import { MyFeedbackPage } from './feedback/MyFeedbackPage';
import { GalleryAppsPage } from './gallery/GalleryAppsPage';
import { GalleryDetailPage } from './gallery/GalleryDetailPage';
import { GalleryPage } from './gallery/GalleryPage';
import { Landing } from './landing/Landing';
import { LessonDetail } from './lessons/LessonDetail';
import { LessonLibrary } from './lessons/LessonLibrary';
import { ModeratorDashboard } from './moderator/ModeratorDashboard';
import { OnboardingPage } from './onboarding/OnboardingPage';
import { browserAudioTrainer } from './ml/browserAudioTrainer';
import { browserPoseTrainer } from './ml/browserPoseTrainer';
import { browserImageTrainer } from './ml/browserTrainer';
import { NotFound } from './NotFound';
import { PortfolioPage } from './portfolio/PortfolioPage';
import { ProfilePage } from './profile/ProfilePage';
import { ProjectsStoreContext } from './stores/projectsContext';
import { makeProjectsStore, type ProjectsStoreApi } from './stores/useProjectsStore';
import { BehaviorView } from './studio/BehaviorView';
import { AdminShell } from './admin/AdminShell';
import { AdminOverview } from './admin/pages/AdminOverview';
import { AdminUsers } from './admin/pages/AdminUsers';
import { AdminUserDetailPage } from './admin/pages/AdminUserDetail';
import { AdminAuditLog } from './admin/pages/AdminAuditLog';
import { AdminClassrooms } from './admin/pages/AdminClassrooms';
import { AdminSubmissions } from './admin/pages/AdminSubmissions';
import { AdminPublishedApps } from './admin/pages/AdminPublishedApps';
import { AdminReports } from './admin/pages/AdminReports';
import { AdminGallery } from './admin/pages/AdminGallery';
import { AdminModerationAudit } from './admin/pages/AdminModerationAudit';
import { AdminConfig } from './admin/pages/AdminConfig';
import { AdminEmailOutbox } from './admin/pages/AdminEmailOutbox';
import { AdminHealth } from './admin/pages/AdminHealth';
import { AdminFlags } from './admin/pages/AdminFlags';
import { AdminMessages } from './admin/pages/AdminMessages';
import { AboutPage } from './marketing/AboutPage';
import { ContactPage } from './marketing/ContactPage';
import { PrivacyPage } from './marketing/PrivacyPage';
import { ManagePublishedPage } from './publish/ManagePublishedPage';
import { PublishView } from './publish/PublishView';
import { ClassroomBroadcastBanner } from './studio/ClassroomBroadcastBanner';
import { ClassroomPauseOverlay } from './studio/ClassroomPauseOverlay';
import { Home } from './studio/Home';
import { initTheme } from './theme/theme';
import { ProjectView } from './studio/ProjectView';
import { ClassGalleryPage } from './teacher/ClassGalleryPage';
import { ClassroomDetail } from './teacher/ClassroomDetail';
import { GradeSubmission } from './teacher/GradeSubmission';
import { TeacherDashboard } from './teacher/TeacherDashboard';

export interface AppProps {
  /** Override the projects store. Used by tests to avoid Dexie/IndexedDB. */
  store?: ProjectsStoreApi;
}

// Apply the persisted theme once at module load so the right
// `data-theme` attribute is on `<html>` before the first paint.
// Calling at module scope (not inside the component) avoids a flash
// of the default palette on routes that mount lazily.
/* v8 ignore start */
if (typeof document !== 'undefined') {
  initTheme();
}
/* v8 ignore stop */

export function App({ store }: AppProps = {}): JSX.Element {
  // The production store is created once per app instance and held by ref so
  // hot-reload and StrictMode double-mount don't recreate Dexie connections.
  // The fallback construction is exercised in Playwright (real Dexie) — it
  // intentionally has no unit-test coverage so disk stays an external boundary.
  const storeRef = useRef<ProjectsStoreApi | null>(store ?? null);
  /* v8 ignore start */
  if (!storeRef.current) {
    storeRef.current = makeProjectsStore({
      trainer: browserImageTrainer,
      audioTrainer: browserAudioTrainer,
      poseTrainer: browserPoseTrainer,
    });
  }
  /* v8 ignore stop */

  return (
    <ReactQueryProvider>
      <ProjectsStoreContext.Provider value={storeRef.current}>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <SuspensionBanner />
          <ClassroomBroadcastBanner />
          <AppRoutes />
          <BadgeToast />
          <ClassroomPauseOverlay />
        </BrowserRouter>
      </ProjectsStoreContext.Provider>
    </ReactQueryProvider>
  );
}

export function AppRoutes(): JSX.Element {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/studio" element={<Home />} />
      <Route path="/studio/p/:projectId" element={<ProjectView />} />
      <Route path="/studio/p/:projectId/behavior" element={<BehaviorView />} />
      <Route path="/studio/p/:projectId/publish" element={<PublishView />} />
      <Route path="/studio/me/published" element={<ManagePublishedPage />} />
      <Route path="/gallery" element={<GalleryPage />} />
      {/* `apps` MUST come before `:id` so the static segment wins. */}
      <Route path="/gallery/apps" element={<GalleryAppsPage />} />
      <Route path="/gallery/:id" element={<GalleryDetailPage />} />
      <Route path="/admin/moderator" element={<ModeratorDashboard />} />
      <Route path="/admin/moderator/:submissionId" element={<ModeratorDashboard />} />
      <Route path="/parent-consent/:token" element={<ParentConsentPage />} />
      <Route path="/public-consent/:token" element={<PublicConsentPage />} />
      <Route path="/magic-link" element={<MagicLinkPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/portfolio" element={<PortfolioPage />} />
      <Route path="/u/:username" element={<ProfilePage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/me/feedback" element={<MyFeedbackPage />} />
      <Route path="/onboarding" element={<OnboardingPage />} />
      <Route path="/teacher" element={<TeacherDashboard />} />
      <Route path="/teacher/lessons" element={<LessonLibrary />} />
      <Route path="/teacher/lessons/:id" element={<LessonDetail />} />
      <Route path="/teacher/classrooms/:id" element={<ClassroomDetail />} />
      <Route path="/teacher/classrooms/:cid/submissions/:sid/grade" element={<GradeSubmission />} />
      <Route path="/class/:cid/gallery" element={<ClassGalleryPage />} />
      {/* Admin console — nested routes share the AdminShell chrome. */}
      <Route path="/admin" element={<AdminShell />}>
        <Route index element={<AdminOverview />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="users/:userId" element={<AdminUserDetailPage />} />
        <Route path="classrooms" element={<AdminClassrooms />} />
        <Route path="submissions" element={<AdminSubmissions />} />
        <Route path="published-apps" element={<AdminPublishedApps />} />
        <Route path="reports" element={<AdminReports />} />
        <Route path="gallery" element={<AdminGallery />} />
        <Route path="audit-log" element={<AdminAuditLog />} />
        <Route path="moderation-audit" element={<AdminModerationAudit />} />
        <Route path="config" element={<AdminConfig />} />
        <Route path="email-outbox" element={<AdminEmailOutbox />} />
        <Route path="health" element={<AdminHealth />} />
        <Route path="flags" element={<AdminFlags />} />
        <Route path="messages" element={<AdminMessages />} />
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
